import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { getProducts, getFacets } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { SlidersHorizontal, X } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [facets, setFacets] = useState({ collections: [], colors: [], sizes: [], materials: [], performance: [] });
  const [price, setPrice] = useState([0, 250]);
  const [mobileFilters, setMobileFilters] = useState(false);
  const [loading, setLoading] = useState(true);

  const collection = params.get("collection") || "All";
  const sort = params.get("sort") || "featured";
  const color = params.get("color");
  const size = params.get("size");
  const material = params.get("material");
  const performance = params.get("performance");

  useEffect(() => { getFacets().then(setFacets); }, []);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    getProducts({ collection: collection === "All" ? undefined : collection, sort, color, size, material, performance,
      min_price: price[0], max_price: price[1] }).then((d) => { setProducts(d); setLoading(false); });
  }, [collection, sort, color, size, material, performance, price]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || next.get(k) === v) next.delete(k); else next.set(k, v);
    setParams(next);
  };
  const clearAll = () => { setParams({}); setPrice([0, 250]); };

  const FilterGroup = ({ title, name, options }) => (
    <AccordionItem value={name} className="border-hairline">
      <AccordionTrigger className="overline text-[11px] hover:no-underline py-4">{title}</AccordionTrigger>
      <AccordionContent className="space-y-3 pb-4">
        {options.map((o) => (
          <label key={o} className="flex items-center gap-3 cursor-pointer" data-testid={`filter-${name}-${o}`}>
            <Checkbox checked={params.get(name) === o} onCheckedChange={() => setParam(name, o)} className="rounded-none border-charcoal" />
            <span className="text-sm font-light">{o}</span>
          </label>
        ))}
      </AccordionContent>
    </AccordionItem>
  );

  const Sidebar = () => (
    <div data-testid="shop-filters">
      <div className="flex items-center justify-between mb-4">
        <span className="overline text-[11px]">Filters</span>
        <button onClick={clearAll} className="overline text-[10px] text-gold" data-testid="clear-filters">Clear All</button>
      </div>
      <Accordion type="multiple" defaultValue={["collection", "color", "size"]} className="w-full">
        <FilterGroup title="Collection" name="collection" options={facets.collections} />
        <FilterGroup title="Color" name="color" options={facets.colors} />
        <FilterGroup title="Size" name="size" options={facets.sizes} />
        <FilterGroup title="Material" name="material" options={facets.materials} />
        <FilterGroup title="Performance" name="performance" options={facets.performance} />
        <AccordionItem value="price" className="border-hairline">
          <AccordionTrigger className="overline text-[11px] hover:no-underline py-4">Price</AccordionTrigger>
          <AccordionContent className="pt-4 pb-6 px-1">
            <div data-testid="price-slider"><Slider min={0} max={250} step={10} value={price} onValueChange={setPrice} /></div>
            <div className="flex justify-between mt-3 text-xs font-display"><span>${price[0]}</span><span>${price[1]}</span></div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );

  return (
    <div data-testid="shop-page">
      <div className="bg-beige/40 py-16">
        <div className="container-luxe">
          <p className="overline text-gold mb-3">Shop</p>
          <h1 className="h-display text-5xl sm:text-6xl">{collection === "All" ? "The Full Collection" : collection}</h1>
          <p className="mt-4 text-sm text-muted-foreground font-light max-w-lg">Premium essentials engineered for movement and made to be worn beyond the studio.</p>
        </div>
      </div>

      <div className="container-luxe py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="hidden lg:block lg:col-span-3"><div className="sticky top-36"><Sidebar /></div></aside>

        <div className="lg:col-span-9">
          <div className="flex items-center justify-between mb-8 border-b border-hairline pb-4">
            <button onClick={() => setMobileFilters(true)} className="lg:hidden inline-flex items-center gap-2 overline text-[11px]" data-testid="mobile-filters-open">
              <SlidersHorizontal size={15} /> Filters
            </button>
            <span className="text-sm text-muted-foreground hidden lg:block">{products.length} items</span>
            <select value={sort} onChange={(e) => setParam("sort", e.target.value)} data-testid="sort-select"
              className="bg-transparent border border-hairline px-4 py-2 text-xs font-display uppercase tracking-wide2 outline-none cursor-pointer">
              <option value="featured">Featured</option>
              <option value="newest">Newest</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">{[...Array(5)].map((_, i) => <div key={i} className="aspect-[4/5] bg-[#EDE6DA] animate-pulse" />)}</div>
          ) : products.length === 0 ? (
            <p className="font-serif text-2xl py-20 text-center" data-testid="no-products">No products match your filters.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-4 gap-y-10" data-testid="product-grid">
              {products.map((p, i) => (<Reveal key={p.id} delay={(i % 3) * 0.06}><ProductCard product={p} /></Reveal>))}
            </div>
          )}
        </div>
      </div>

      {mobileFilters && (
        <div className="fixed inset-0 z-[80] bg-bone p-6 overflow-y-auto lg:hidden" data-testid="mobile-filters-panel">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif text-2xl">Filters</h3>
            <button onClick={() => setMobileFilters(false)} data-testid="mobile-filters-close"><X size={24} /></button>
          </div>
          <Sidebar />
          <button onClick={() => setMobileFilters(false)} className="btn-primary w-full mt-8">Show {products.length} Results</button>
        </div>
      )}
    </div>
  );
}
